import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import InputText from '../Common/InputText';
import TextArea from '../Common/TextArea';
import ImageChooser from '../Common/ImageChooser/ImageChooser';
import ColorChooser from '../Common/ColorChooser';
import MultiSelectBox from '../Common/MultiSelectBox/MultiSelectBox';
import Toggle from '../Common/Toggle/Toggle';
import Button from '../Common/Button';
import DatePicker from '../Common/DatePicker/DatePicker';
import { Editor } from 'react-draft-wysiwyg';
import './SubscriptionForm.scss';
import SubscriptionGoals from './SubscriptionGoals';

const SubscriptionForm = (props) => {
	let toolbar = {
		options: ['inline', 'fontSize', 'fontFamily', 'textAlign', 'colorPicker', 'link', 'emoji'],
		inline: {
			options: ['bold', 'italic', 'underline'],
		},
	};

	const onFAQChange = (faq, index, field, event) => {
		let faqs = props.subscription.faqs.slice();
		faq[field] = event.target.value;
		faqs.splice(index, 1, faq);
		props.updateSubscription('faqs', faqs);
	};

	// v4 data workaround
	let welcomeMessageJson = null;
	if (props.subscription.welcomeMessage) {
		try {
			welcomeMessageJson = JSON.parse(props.subscription.welcomeMessage);
		} catch (error) {
			// Ignore v4 HTML message
		}
	}

	let approvedMessageJson = null;
	if (props.subscription.approvedMessage) {
		try {
			approvedMessageJson = JSON.parse(props.subscription.approvedMessage);
		} catch (error) {
			// Ignore v4 HTML message
		}
	}

	let emailMessageJson = null;
	if (props.subscription.emailMessage) {
		try {
			emailMessageJson = JSON.parse(props.subscription.emailMessage);
		} catch (error) {
			// Ignore v4 HTML message
		}
	}
	const updateDefaultAgent = (id) => {
		const newValue = id ? id : '';
		props.updateSubscription('defaultAgentUserId', newValue);
	};

	const updateBillingPlan = (id) => {
		const newValue = id ? id : '';
		props.updateSubscription('billingPlanId', newValue);
	};

	const onTrialEndDateChange = (date) => {
		props.updateSubscription('trialEndDate', date);
	};

	return (
		<form id='subForm' onSubmit={props.onSubmitForm} noValidate className={props.isFormValid ? '' : 'was-validated'}>
			<div className='row'>
				<div className='subform-inputs-toprow col-sm-12 col-xl-4'>
					<InputText
						name='name'
						label='Subscription Name'
						type='text'
						placeholder='Subscription Name'
						onChange={props.onInputChange}
						value={props.subscription.name}
						isRequired={true}
						errorMessage='A Subscription Name is required'
					/>
				</div>
				<div className='subform-inputs-toprow col-sm-12 col-xl-4'>
					<InputText
						name='accessCode'
						label='User Request Access Code'
						type='text'
						placeholder='User Request Access Code'
						onChange={props.onInputChange}
						value={props.subscription.accessCode}
						isRequired={true}
						errorMessage='User Request Access Code is required'
					/>
					{!props.isAccessCodeFormValid && !props.isFormValid && (
						<p className='accessCodeRules'>
							Access code must contain at least 1 letter, 1 number or special character, and be at least 7 characters long.{' '}
						</p>
					)}
					{!props.isAccessCodeResponseValid && <p className='accessCodeRules'>Access code cannot be a common password</p>}
				</div>
			</div>

			<div className='row'>
				<div className='subform-inputs-secondrow col-sm-12 col-xl-4'>
					<MultiSelectBox
						name='defaultAgentUserId'
						label='Assign new support tickets to'
						idField='id'
						valueField='name'
						options={props.supportAgents}
						selectedValues={props.subscription.defaultAgentUserId.toString()}
						onChange={updateDefaultAgent}
						isRequired={false}
						isMulti={false}
					/>
				</div>
				<div className='subform-inputs-secondrow col-sm-12 col-xl-4'>
					<InputText
						name='businessContactEmails'
						label='Billing Contact Emails'
						type='text'
						placeholder='Billing Contact Emails'
						onChange={props.onInputChange}
						value={props.subscription.businessContactEmails}
					/>
				</div>
				<div className='subform-inputs-secondrow col-sm-12 col-xl-4'>
					<InputText
						name='reportPageFooter'
						label='Report Page Footer'
						type='text'
						placeholder='Report Page Footer'
						onChange={props.onInputChange}
						value={props.subscription.reportPageFooter}
					/>
				</div>
			</div>

			<div className='row'>
				<div className='subform-inputs-secondrow col-sm-12 col-xl-4'>
					<InputText
						isDisabled={true}
						name='totalTasks'
						label='Total Inflight Tasks'
						type='text'
						placeholder='Total Inflight Tasks'
						onChange={props.onInputChange}
						value={props.totalTasks.toString()}
					/>
				</div>
				<div className='subform-inputs-secondrow col-sm-12 col-xl-4'>
					<MultiSelectBox
						name='billingPlanId'
						label='Billing Plan'
						idField='id'
						valueField='name'
						options={props.billingPlans}
						selectedValues={props.subscription.billingPlanId.toString()}
						onChange={updateBillingPlan}
						isRequired={true}
						isMulti={false}
					/>
				</div>
			</div>

			<br />

			<div className='row'>
				<div className='subform-toggle-row col-sm-12 col-xl-4'>
					<Toggle name='isActive' text='Subscription Active' checked={props.subscription.isActive} onChange={props.onActiveChange} />
					<Toggle
						name='isProjectWidgetActive'
						text='Project Widgets Active'
						checked={props.subscription.isProjectWidgetActive}
						onChange={props.onActiveChange}
					/>
					{props.subscription.isProjectWidgetActive && (
						<Fragment>
							<Toggle
								name='isProjectIdRequiredForLocationTask'
								text='Require Project selection for all tasks'
								checked={props.subscription.isProjectIdRequiredForLocationTask}
								onChange={props.onActiveChange}
							/>
							<Toggle
								name='isProjectWaiversActive'
								text='Project Waivers'
								checked={props.subscription.isProjectWaiversActive}
								onChange={props.onActiveChange}
							/>
							<Toggle
								name='isProjectReferenceDatesActive'
								text='Project Reference Dates'
								checked={props.subscription.isProjectReferenceDatesActive}
								onChange={props.onActiveChange}
							/>
						</Fragment>
					)}
					<Toggle
						name='isGoalWidgetActive'
						text='Goals Widget Active'
						checked={props.subscription.isGoalWidgetActive}
						onChange={props.onActiveChange}
					/>
					<Toggle
						name='isWelcomeMessageActive'
						text='Welcome Message Active'
						checked={props.subscription.isWelcomeMessageActive}
						onChange={props.onActiveChange}
					/>
					{props.subscription.isWelcomeMessageActive ? (
						<Toggle
							name='isWelcomeMessageConstant'
							text='Display Welcome Message Every Login'
							checked={props.subscription.isWelcomeMessageConstant}
							onChange={props.onActiveChange}
						/>
					) : (
						''
					)}
				</div>
				<div className='subform-toggle-row col-sm-12 col-xl-4'>
					<Toggle
						name='isStartDateActive'
						text='Start Date Active'
						checked={props.subscription.isStartDateActive}
						onChange={props.onActiveChange}
					/>
					<Toggle
						name='isTaskDependenciesActive'
						text='Task Dependencies Active'
						checked={props.subscription.isTaskDependenciesActive}
						onChange={props.onActiveChange}
					/>
					<Toggle
						name='isTaskStepsActive'
						text='Task Steps Active'
						checked={props.subscription.isTaskStepsActive}
						onChange={props.onActiveChange}
					/>
					<Toggle
						name='isTaskEffortActive'
						text='Task Effort Active'
						checked={props.subscription.isTaskEffortActive}
						onChange={props.onActiveChange}
					/>
					<Toggle
						name='isTaskFeedbackActive'
						text='Task Feedback Active'
						checked={props.subscription.isTaskFeedbackActive}
						onChange={props.onActiveChange}
					/>
					<Toggle
						name='isTaskFinancialsActive'
						text='Task Financials Active'
						checked={props.subscription.isTaskFinancialsActive}
						onChange={props.onActiveChange}
					/>
				</div>
				<div className='subform-toggle-row col-sm-12 col-xl-4'>
					<Toggle name='isSSOEnabled' text='SSO Enabled' checked={props.subscription.isSSOEnabled} onChange={props.onActiveChange} />
					<Toggle
						name='isSSOAutoActivate'
						text='SSO Auto-activate Inactive User Profile'
						checked={props.subscription.isSSOAutoActivate}
						onChange={props.onActiveChange}
					/>
					<Toggle
						name='isSSOAutoCreate'
						text='SSO Auto-create New User Profile'
						checked={props.subscription.isSSOAutoCreate}
						onChange={props.onActiveChange}
					/>
					<Toggle
						name='isLanguageTranslationActive'
						text='Language Translation'
						checked={props.subscription.isLanguageTranslationActive}
						onChange={props.onActiveChange}
					/>
					<Toggle
						name='isBaselineTaskValidationRulesActive'
						text='Baseline Task Validation Rules'
						checked={props.subscription.isBaselineTaskValidationRulesActive}
						onChange={props.onActiveChange}
					/>
					<Toggle name='isWebhooksActive' text='Webhooks' checked={props.subscription.isWebhooksActive} onChange={props.onActiveChange} />
					<Toggle
						name='isSaveAndManageTaskFiltersActive'
						text='Save and Manage Task Filters'
						checked={props.subscription.isSaveAndManageTaskFiltersActive}
						onChange={props.onActiveChange}
					/>
					<Toggle name='isTrial' text='Trial' checked={props.subscription.isTrial} onChange={props.onActiveChange} />
					{props.subscription.isTrial && (
						<DatePicker
							showIcon
							value={props.subscription.trialEndDate ? new Date(props.subscription.trialEndDate) : new Date()}
							name='trialEndDate'
							label='Trial End Date'
							onChange={onTrialEndDateChange}
						/>
					)}
				</div>
			</div>

			<br />

			<div className='row'>
				<div className='subform-toggle-row col-sm-12 col-xl-4'>
					<ColorChooser
						title='Logo Background Color'
						name='logoColor'
						selectedColor={props.subscription.logoColor}
						onChange={props.onLogoColorChange}
					/>
					<ColorChooser
						title='HTML Highlight Color'
						name='highlightColor'
						selectedColor={props.subscription.highlightColor}
						onChange={props.onHighlightColorChange}
					/>
				</div>
				<div className='subform-toggle-row col-sm-12 col-xl-4'>
					<ImageChooser
						name='logo'
						title='Upload Subscription Logo'
						onChange={props.onLogoChange}
						backgroundColor={props.subscription.logoColor}
						defaultImage={props.image}
					/>
				</div>
			</div>
			<br />
			<strong>Welcome Message</strong>
			<Editor
				spellCheck={true}
				toolbarClassName='toolbarClassName'
				wrapperClassName='wrapperClassName'
				editorClassName='editorClassName'
				toolbar={toolbar}
				onEditorStateChange={props.onWelcomeMessageChange}
				defaultContentState={welcomeMessageJson}
			/>
			{props.showWelcomeMessageRequired && !props.isFormValid ? (
				<div name='showWelcomeMessageRequired' className='text-danger'>
					Welcome Message is required if this setting is set to Active.
				</div>
			) : (
				''
			)}
			<br />
			<strong>New User Approved Email Message</strong>
			<Editor
				spellCheck={true}
				toolbarClassName='toolbarClassName'
				wrapperClassName='wrapperClassName'
				editorClassName='editorClassName'
				toolbar={toolbar}
				onEditorStateChange={props.onApprovedMessageChange}
				defaultContentState={approvedMessageJson}
			/>
			{props.showApprovedMessageRequired && !props.isFormValid ? (
				<div name='showApprovedMessageRequired' className='text-danger'>
					User Approved Message is required.
				</div>
			) : (
				''
			)}
			<br />
			<strong>New User Invitation Email Message</strong>
			<Editor
				spellCheck={true}
				toolbarClassName='toolbarClassName'
				wrapperClassName='wrapperClassName'
				editorClassName='editorClassName'
				toolbar={toolbar}
				onEditorStateChange={props.onEmailMessageChange}
				defaultContentState={emailMessageJson}
			/>
			{props.showUserInviteMessageRequired && !props.isFormValid ? (
				<div name='showUserInviteMessageRequired' className='text-danger'>
					User Invitation Email is required.
				</div>
			) : (
				''
			)}
			<br />

			<div className='row'>
				<div className='col-12'>
					<strong>FAQ Questions and Answers</strong>
				</div>

				{props.subscription.faqs.map((faq, index) => {
					return (
						<div key={faq.ordinal} className={`col-sm-12 col-lg-6 subscription-question${index + 1}`}>
							<InputText
								name='question'
								label={`Question ${index + 1}`}
								value={faq.question}
								placeholder='Question'
								onChange={(e) => onFAQChange(faq, index, 'question', e)}
								type='text'
								maxLength={256}
							/>
							<TextArea
								rows={4}
								name='answer'
								label={`Answer ${index + 1}`}
								value={faq.answer}
								placeholder='Answer'
								onChange={(e) => onFAQChange(faq, index, 'answer', e)}
								maxLength={1000}
							/>
							<Button
								name={`remove-question${index + 1}`}
								type='button'
								bootstrapClass='link question-remove'
								text='Remove'
								onClick={() => props.removeQuestion(index)}
							/>
						</div>
					);
				})}
				<hr />
				{props.subscription.faqs.length < props.maxNumFAQs ? (
					<div className='col-12'>
						<Button
							name='question-add'
							type='button'
							bootstrapClass='link question-add'
							text='Add Question'
							onClick={props.addQuestion}
						/>
					</div>
				) : (
					<div className='col-12 mb-2'></div>
				)}
				<br />
				<br />
			</div>
			{props.subscription.isGoalWidgetActive && (
				<SubscriptionGoals
					subscription={props.subscription}
					removeGoalGroup={props.removeGoalGroup}
					maxNumGoalGroups={props.maxNumGoalGroups}
					updateSubscription={props.updateSubscription}
				/>
			)}

			<Button type='submit' bootstrapClass='primary' text='Save' onClick={props.onSubmitForm} />
			<Button type='button' bootstrapClass='light' text='Cancel' onClick={props.onCancelForm} />
		</form>
	);
};
SubscriptionForm.propTypes = {
	subscription: PropTypes.object.isRequired,
	supportAgents: PropTypes.array,
	billingPlans: PropTypes.array,
	totalTasks: PropTypes.number,
	image: PropTypes.string,
	addQuestion: PropTypes.func.isRequired,
	removeQuestion: PropTypes.func.isRequired,
	removeGoalGroup: PropTypes.func.isRequired,
	maxNumFAQs: PropTypes.number.isRequired,
	maxNumGoalGroups: PropTypes.number.isRequired,
	updateSubscription: PropTypes.func.isRequired,
	onSubmitForm: PropTypes.func.isRequired,
	onCancelForm: PropTypes.func.isRequired,
	onInputChange: PropTypes.func.isRequired,
	onActiveChange: PropTypes.func.isRequired,
	onLogoColorChange: PropTypes.func.isRequired,
	onHighlightColorChange: PropTypes.func.isRequired,
	onLogoChange: PropTypes.func.isRequired,
	onWelcomeMessageChange: PropTypes.func.isRequired,
	onApprovedMessageChange: PropTypes.func.isRequired,
	onEmailMessageChange: PropTypes.func.isRequired,
	isAccessCodeResponseValid: PropTypes.bool.isRequired,
	isAccessCodeFormValid: PropTypes.bool.isRequired,
	isFormValid: PropTypes.bool.isRequired,
	showWelcomeMessageRequired: PropTypes.bool.isRequired,
	showApprovedMessageRequired: PropTypes.bool.isRequired,
	showUserInviteMessageRequired: PropTypes.bool.isRequired,
};

export default SubscriptionForm;
